import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import NProgress from "nprogress";

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: "/",
      redirect: "/dashboard",
    },
    {
      path: "/login",
      name: "Login",
      component: () => import("@/views/auth/LoginView.vue"),
      meta: {
        requiresAuth: false,
        title: "Login - DerraCare",
      },
    },
    {
      path: "/register",
      name: "Register",
      component: () => import("@/views/auth/RegisterView.vue"),
      meta: {
        requiresAuth: false,
        title: "Register - DerraCare",
      },
    },
    {
      path: "/verify-email",
      name: "EmailVerification",
      component: () => import("@/views/auth/EmailVerificationView.vue"),
      meta: {
        requiresAuth: false,
        title: "Verify Email - DerraCare",
      },
    },
    {
      path: "/forgot-password",
      name: "ForgotPassword",
      component: () => import("@/views/auth/ForgotPasswordView.vue"),
      meta: {
        requiresAuth: false,
        title: "Forgot Password - DerraCare",
      },
    },
    {
      path: "/reset-password",
      name: "ResetPassword",
      component: () => import("@/views/auth/ResetPasswordView.vue"),
      meta: {
        requiresAuth: false,
        title: "Reset Password - DerraCare",
      },
    },
    {
      path: "/onboarding",
      name: "Onboarding",
      component: () => import("@/views/onboarding/OnboardingView.vue"),
      meta: {
        requiresAuth: true,
        title: "Complete Setup - DerraCare",
      },
    },
    {
      path: "/dashboard",
      name: "Dashboard",
      component: () => import("@/views/dashboard/DashboardView.vue"),
      meta: {
        requiresAuth: true,
        portal: "doctor",
        title: "Dashboard - DerraCare",
      },
    },
    {
      path: "/dashboard/patients",
      name: "DashboardPatients",
      component: () => import("@/views/dashboard/DoctorPatientsView.vue"),
      meta: {
        requiresAuth: true,
        portal: "doctor",
        title: "Patients - DerraCare",
      },
    },
    {
      path: "/dashboard/appointments",
      name: "DashboardAppointments",
      component: () => import("@/views/dashboard/DoctorAppointmentsView.vue"),
      meta: {
        requiresAuth: true,
        portal: "doctor",
        title: "Appointments - DerraCare",
      },
    },
    {
      path: "/dashboard/messages",
      name: "DashboardMessages",
      component: () => import("@/views/dashboard/DoctorMessagesView.vue"),
      meta: {
        requiresAuth: true,
        portal: "doctor",
        title: "Messages - DerraCare",
      },
    },
    {
      path: "/dashboard/billing",
      name: "DashboardBilling",
      component: () => import("@/views/dashboard/DoctorBillingView.vue"),
      meta: {
        requiresAuth: true,
        portal: "doctor",
        title: "Billing - DerraCare",
      },
    },
    {
      path: "/dashboard/profile",
      name: "DashboardProfile",
      component: () => import("@/views/dashboard/DoctorProfileView.vue"),
      meta: {
        requiresAuth: true,
        portal: "doctor",
        title: "Profile - DerraCare",
      },
    },
    {
      path: "/dashboard/activity",
      name: "DashboardActivity",
      component: () => import("@/views/dashboard/DashboardActivityView.vue"),
      meta: {
        requiresAuth: true,
        portal: "doctor",
        title: "Activity & Reports - DerraCare",
      },
    },
    {
      path: "/dashboard/reports",
      name: "DashboardReports",
      component: () => import("@/views/dashboard/DoctorReportsView.vue"),
      meta: {
        requiresAuth: true,
        portal: "doctor",
        title: "Reports - DerraCare",
      },
    },
    {
      path: "/dashboard/appointments-clients",
      name: "AppointmentsClients",
      component: () => import("@/views/dashboard/AppointmentsClientsView.vue"),
      meta: {
        requiresAuth: true,
        title: "Appointments & Clients - DerraCare",
      },
    },
    {
      path: "/profile",
      name: "Profile",
      component: () => import("@/views/profile/ProfileView.vue"),
      meta: {
        requiresAuth: true,
        title: "Profile - DerraCare",
      },
    },
    {
      path: "/patients",
      name: "Patients",
      component: () => import("@/views/patients/PatientsView.vue"),
      meta: {
        requiresAuth: true,
        title: "Patients - DerraCare",
      },
    },
    {
      path: "/appointments",
      name: "Appointments",
      component: () => import("@/views/appointments/AppointmentsView.vue"),
      meta: {
        requiresAuth: true,
        title: "Appointments - DerraCare",
      },
    },
    {
      path: "/settings",
      name: "Settings",
      component: () => import("@/views/settings/SettingsView.vue"),
      meta: {
        requiresAuth: true,
        title: "Settings - DerraCare",
      },
    },
    {
      path: "/reports/print",
      name: "PrintReports",
      component: () => import("@/views/reports/PrintReportsView.vue"),
      meta: {
        requiresAuth: true,
        title: "Print Reports - DerraCare",
      },
    },
    // Legal Document Routes
    {
      path: "/terms-of-service",
      name: "TermsOfService",
      component: () => import("@/views/legal/TermsOfServiceView.vue"),
      meta: {
        requiresAuth: false,
        title: "Terms of Service - DerraCare",
      },
    },
    {
      path: "/privacy-policy",
      name: "PrivacyPolicy",
      component: () => import("@/views/legal/PrivacyPolicyView.vue"),
      meta: {
        requiresAuth: false,
        title: "Privacy Policy - DerraCare",
      },
    },
    {
      path: "/medical-disclaimer",
      name: "MedicalDisclaimer",
      component: () => import("@/views/legal/MedicalDisclaimerView.vue"),
      meta: {
        requiresAuth: false,
        title: "Medical Practice Guidelines - DerraCare",
      },
    },
    {
      path: "/whatsapp-verification",
      name: "WhatsAppVerification",
      component: () => import("@/views/auth/WhatsAppVerificationView.vue"),
      meta: {
        requiresAuth: true, // Requires login to verify WhatsApp
        title: "WhatsApp Verification - DerraCare",
      },
    },
    // Admin Routes
    {
      path: "/admin/login",
      name: "AdminLogin",
      component: () => import("@/views/admin/AdminLoginView.vue"),
      meta: {
        requiresAuth: false,
        portal: "admin",
        title: "Admin Login - DerraCare",
      },
    },
    {
      path: "/admin/dashboard",
      name: "AdminDashboard",
      component: () => import("@/views/admin/AdminDashboardView.vue"),
      meta: {
        requiresAuth: true,
        requiresAdmin: true,
        portal: "admin",
        title: "Admin Dashboard - DerraCare",
      },
    },
    {
      path: "/admin/patients",
      name: "AdminPatients",
      component: () => import("@/views/admin/AdminPatientsView.vue"),
      meta: {
        requiresAuth: true,
        requiresAdmin: true,
        portal: "admin",
        title: "Patient Management - DerraCare",
      },
    },
    {
      path: "/admin/settings",
      name: "AdminSettings",
      component: () => import("@/views/admin/AdminSettingsView.vue"),
      meta: {
        requiresAuth: true,
        requiresAdmin: true,
        portal: "admin",
        title: "System Settings - DerraCare",
      },
    },
    {
      path: "/admin/news",
      name: "AdminNews",
      component: () => import("@/views/admin/AdminNewsView.vue"),
      meta: {
        requiresAuth: true,
        requiresAdmin: true,
        portal: "admin",
        title: "News Management - DerraCare",
      },
    },
    {
      path: "/admin/system-reports",
      name: "AdminSystemReports",
      component: () => import("@/views/admin/SystemReportsView.vue"),
      meta: {
        requiresAuth: true,
        requiresAdmin: true,
        portal: "admin",
        title: "System Reports - DerraCare",
      },
    },
    {
      path: "/admin/training-management",
      name: "AdminTraining",
      component: () => import("@/views/admin/TrainingManagementView.vue"),
      meta: {
        requiresAuth: true,
        requiresAdmin: true,
        portal: "admin",
        title: "Training Hub - DerraCare Admin",
      },
    },
    {
      path: "/admin/doctors",
      name: "AdminDoctors",
      component: () => import("@/views/admin/DoctorActivityDashboard.vue"),
      meta: {
        requiresAuth: true,
        requiresAdmin: true,
        portal: "admin",
        title: "Doctor Activity - DerraCare",
      },
    },
    {
      path: "/admin/state-admins",
      name: "StateAdminManagement",
      component: () => import("@/views/admin/StateAdminManagementView.vue"),
      meta: {
        requiresAuth: true,
        requiresAdmin: true,
        requiresSuperAdmin: true, // SUPER ADMIN ONLY
        portal: "admin",
        title: "State Admin Management - DerraCare",
      },
    },
    {
      path: "/admin/reports/detailed",
      name: "AdminDetailedReports",
      component: () => import("@/views/admin/AdminDetailedReportsView.vue"),
      meta: {
        requiresAuth: true,
        requiresAdmin: true,
        portal: "admin",
        title: "Detailed Reports - DerraCare Admin",
      },
    },
    {
      path: "/admin/finance/audit",
      name: "AdminFinancialAudit",
      component: () => import("@/views/admin/FinancialAuditView.vue"),
      meta: {
        requiresAuth: true,
        requiresAdmin: true,
        portal: "admin",
        title: "Financial Audit & Compliance - DerraCare Admin",
      },
    },
    {
      path: "/admin/safety-incidents",
      name: "AdminSafetyIncidents",
      component: () => import("@/views/admin/SafetyIncidentsView.vue"),
      meta: {
        requiresAuth: true,
        requiresAdmin: true,
        portal: "admin",
        title: "Safety Incidents - DerraCare Admin",
      },
    },
    {
      path: "/admin/safety-index",
      name: "AdminSafetyIndex",
      component: () => import("@/views/admin/SafetyIndexView.vue"),
      meta: {
        requiresAuth: true,
        requiresAdmin: true,
        portal: "admin",
        title: "Nigeria Safety Index - DerraCare Admin",
      },
    },
    {
      path: "/admin/safety-analytics",
      name: "AdminSafetyAnalytics",
      component: () => import("@/views/admin/SafetyAnalyticsView.vue"),
      meta: {
        requiresAuth: true,
        requiresAdmin: true,
        portal: "admin",
        title: "Safety Analytics - DerraCare Admin",
      },
    },
    {
      path: "/admin/data-visualization",
      name: "AdminDataVisualization",
      component: () => import("@/views/admin/SafetyDataVisualizationView.vue"),
      meta: {
        requiresAuth: true,
        requiresAdmin: true,
        portal: "admin",
        title: "Data Visualization & Metrics - DerraCare Admin",
      },
    },
    {
      path: "/admin/question-bank",
      name: "AdminQuestionBank",
      component: () => import("@/views/admin/QuestionBankManagementView.vue"),
      meta: {
        requiresAuth: true,
        requiresAdmin: true,
        requiresSuperAdmin: true, // SUPER ADMIN ONLY
        portal: "admin",
        title: "Question Bank Management - DerraCare Admin",
      },
    },
    {
      path: "/admin/certificates",
      name: "AdminCertificates",
      component: () => import("@/views/admin/CertificateManagementView.vue"),
      meta: {
        requiresAuth: true,
        requiresAdmin: true,
        portal: "admin",
        title: "Certificate Management - DerraCare Admin",
      },
    },
    {
      path: "/verify-password",
      name: "VerifyPassword",
      component: () => import("@/views/auth/VerifyPasswordView.vue"),
      meta: {
        requiresAuth: true,
        title: "Verify Password - DerraCare",
      },
    },
    {
      path: "/reset-password",
      name: "ResetPassword",
      component: () => import("@/views/auth/ResetPasswordView.vue"),
      meta: {
        requiresAuth: false,
        title: "Reset Password - DerraCare",
      },
    },
    {
      path: "/:pathMatch(.*)*",
      name: "NotFound",
      component: () => import("@/views/NotFoundView.vue"),
      meta: {
        title: "Page Not Found - DerraCare",
      },
    },
  ],
});

// Global navigation guards
router.beforeEach(async (to, from, next) => {
  NProgress.start();

  const authStore = useAuthStore();
  const { useAdminStore } = await import("@/stores/admin");
  const adminStore = useAdminStore();

  // Set page title
  if (to.meta.title) {
    document.title = to.meta.title as string;
  }

  const targetPortal = to.meta.portal as string | undefined;

  // ADMIN PORTAL ROUTES
  if (to.meta.requiresAdmin || targetPortal === "admin") {
    // Check admin authentication status if token exists
    if (
      !adminStore.isAuthenticated &&
      (localStorage.getItem("admin_token") ||
        sessionStorage.getItem("admin_token"))
    ) {
      await adminStore.checkAuthStatus();
    }

    // Require admin authentication for admin routes
    if (to.meta.requiresAdmin && !adminStore.isAuthenticated) {
      next({
        name: "AdminLogin",
        query: { redirect: to.fullPath },
      });
      return;
    }

    // SUPER ADMIN ONLY ROUTES - Check admin role
    if (to.meta.requiresSuperAdmin) {
      const admin = adminStore.admin;

      // Check if user has super admin role
      if (!admin || admin.role !== "super_admin") {
        console.warn("Access denied: Super admin privileges required");
        next({
          name: "AdminDashboard",
          query: {
            error: "super_admin_required",
            message: "This page requires super admin privileges",
          },
        });
        return;
      }
    }

    // STATE ADMIN RESTRICTIONS - Only allow access to Safety Incidents and Settings
    if (adminStore.admin?.role === "state_admin") {
      const allowedPaths = [
        "/admin/safety-incidents",
        "/admin/settings",
        "/admin/login", // Allow logout redirect
      ];

      // Check if the route path starts with any allowed path
      const isAllowedPath = allowedPaths.some((path) =>
        to.path.startsWith(path)
      );

      if (!isAllowedPath && to.name !== "AdminLogin") {
        console.warn(
          "Access denied: State admins can only access Safety Incidents"
        );
        next({
          name: "AdminDashboard", // This will redirect to safety-incidents via dashboard logic
          query: {
            error: "access_restricted",
            message:
              "State admins can only access Safety Incidents for approval",
          },
        });
        return;
      }
    }

    // Redirect already logged in admin away from login page
    if (to.name === "AdminLogin" && adminStore.isAuthenticated) {
      next({ name: "AdminDashboard" });
      return;
    }

    next();
    return;
  }

  // DOCTOR PORTAL ROUTES
  if (
    (targetPortal === "doctor" || to.meta.requiresAuth) &&
    targetPortal !== "admin"
  ) {
    // Prevent doctor access if admin is logged in
    if (adminStore.isAuthenticated) {
      console.warn("Admin user cannot access doctor portal");
      next({ name: "AdminDashboard" });
      return;
    }

    // Check doctor authentication
    if (to.meta.requiresAuth && !authStore.isAuthenticated) {
      next({
        name: "Login",
        query: { redirect: to.fullPath },
      });
      return;
    }

    // Check email verification
    if (
      authStore.isAuthenticated &&
      !authStore.isVerified &&
      to.name !== "EmailVerification"
    ) {
      next({
        name: "EmailVerification",
        query: { email: authStore.doctor?.email },
      });
      return;
    }

    // Check onboarding completion
    if (
      authStore.isAuthenticated &&
      authStore.onboardingRequired &&
      to.name !== "Onboarding"
    ) {
      next({ name: "Onboarding" });
      return;
    }

    // Redirect authenticated doctor away from auth pages
    if (
      authStore.isAuthenticated &&
      ["Login", "Register", "ForgotPassword", "ResetPassword"].includes(
        to.name as string,
      )
    ) {
      next({ name: "Dashboard" });
      return;
    }

    next();
    return;
  }

  // PUBLIC ROUTES (no portal specified)
  next();
});

router.afterEach(() => {
  NProgress.done();
});

export default router;
